import { useCallback, useMemo } from "react";

import useUserStore from "@store/useUserStore";
import useSystemStore from "@store/useSystemStore";
import usePlatformStore from "@store/usePlatformStore";
import {
  isValidGoogleCalendarUrl,
  isValidGoogleMailUrl,
  isValidOutlookUrl,
  validateEmailMismatch,
} from "@platform/utils/chrome.util";
import { GrantProvider } from "@app-types/common.type";
import { ActiveScreen } from "@enums/screens.enum";
import { ItemType } from "@enums/common.enum";

export function usePlatformNavigation() {
  const { activeUser, hasSession } = useUserStore();
  const { prevItemType, setPrevItemType } = usePlatformStore();
  const { changeActiveScreen, setIsAppLoading } = useSystemStore();

  const googleGrantEmail = useMemo(
    () => activeUser?.grants?.find((g) => g.provider === GrantProvider.GOOGLE)?.username ?? null,
    [activeUser]
  );

  const navigateInChrome = useCallback(
    async (tabUrl: string, itemType: ItemType | null): Promise<boolean> => {
      const isOutlookUrl = isValidOutlookUrl(tabUrl);
      const isGoogleMailUrl = isValidGoogleMailUrl(tabUrl);
      const isGoogleCalendarUrl = isValidGoogleCalendarUrl(tabUrl);

      // --- AUTH & LOGIN ---
      if (!activeUser && !googleGrantEmail) {
        if (!hasSession) {
          changeActiveScreen(ActiveScreen.LOGIN);
        }
        setPrevItemType(null);
        return false;
      }

      // --- EMAIL MISMATCH ---
      if (isGoogleMailUrl || isGoogleCalendarUrl) {
        const hasMismatch = await validateEmailMismatch(googleGrantEmail);
        if (hasMismatch) {
          changeActiveScreen(ActiveScreen.EMAIL_MISMATCH);
          setPrevItemType(null);
          return false;
        }
      }

      // --- EMAIL ---
      if (itemType === ItemType.EMAIL) {
        setIsAppLoading(true); // for smooth transition to new email
        setPrevItemType(itemType);
        return true;
      }

      // --- MEETING ---
      if (itemType === ItemType.MEETING) {
        setIsAppLoading(true); // for smooth transition to new appointment
        setPrevItemType(itemType);
        return true;
      }

      // --- COMPANY ---
      if (itemType === ItemType.COMPANY) {
        changeActiveScreen(ActiveScreen.COMPANY);
      }

      // --- GOOGLE MAIL ---
      if (isGoogleMailUrl) {
        if (prevItemType === ItemType.EMAIL) {
          setPrevItemType(null);
          return false;
        }
        changeActiveScreen(ActiveScreen.GOOGLE_MAIL);
      }

      // --- GOOGLE CALENDAR ---
      if (isGoogleCalendarUrl) {
        if (prevItemType === ItemType.MEETING) {
          setPrevItemType(null);
          return false;
        }
        changeActiveScreen(ActiveScreen.GOOGLE_CALENDAR);
      }

      // --- OUTLOOK ADD-IN ---
      if (isOutlookUrl) {
        changeActiveScreen(ActiveScreen.GET_OUTLOOK_ADDIN);
      }

      setPrevItemType(itemType);
      return true;
    },
    [activeUser, googleGrantEmail, prevItemType, hasSession]
  );

  return { navigateInChrome };
}
